import { EditOutlined, EyeOutlined, FormOutlined, StarFilled, UserOutlined, } from "@ant-design/icons";
import { Avatar, Button, Card, Checkbox, Col, Input, Row, Space, Tooltip, Typography } from "antd";
import React, { useCallback, useEffect, useMemo, useRef, useState, } from "react";
import { useNavigate } from "react-router-dom";
import { AutoSizer, CellMeasurer, CellMeasurerCache, List, } from "react-virtualized";
import { Loading } from "../../../components/Loading";
import { TreeSelectLocationUser } from "../../../components/TreeSelectLocation";
import { TreeSelectTopic } from "../../../components/TreeSelectTopic";
import { useAuth } from "../../../hooks/auth";
import Api from "../../../services/api";
import FormDetails from "./formDetails";
import { t } from "i18next";
import { useTranslation } from "react-i18next";

const { Title, Text } = Typography;
const cardStyle = { height: "100%" };
const bodyCardStyle = { padding: 16 };

const FilterBar = React.memo(({
  searchText,
  onSearchTextChange,
  topicId,
  onTopicChange,
  topicOpen,
  onTopicOpenChange,
  locationId,
  onLocationChange,
  adhocOnly,
  onAdhocChange,
  showFavoritesOnly,
  onFavoritesChange,
  myFormsOnly,
  onMyFormsChange,
  t
}) => (
  <>
    <Row gutter={8} style={{ marginTop: 8 }}>
      <Col span={8}>
        <Tooltip title="Buscar por nome ou código do formulário">
          <Input
            placeholder="Buscar formulário..."
            allowClear
            value={searchText}
            onChange={e => onSearchTextChange(e.target.value)}
          />
        </Tooltip>
      </Col>
      <Col span={8}>
        <Tooltip title="Filtrar por tópico relacionado">
          <TreeSelectTopic
            open={topicOpen}
            onDropdownVisibleChange={onTopicOpenChange}
            placeholder="Tópico"
            allowClear
            value={topicId}
            onChange={onTopicChange}
            style={{ width: "100%" }}
          />
        </Tooltip>
      </Col>
      <Col span={8}>
        <Tooltip title="Filtrar por localização">
          <TreeSelectLocationUser
            placeholder="Localização"
            allowClear
            includeDirectParents={false}
            value={locationId}
            onChange={onLocationChange}
            style={{ width: "100%" }}
          />
        </Tooltip>
      </Col>
    </Row>
    <Row gutter={8} style={{ marginTop: 8 }}>
      <Col
        span={24}
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 8,
        }}
      >
        <Tooltip title="Exibir apenas formulários criados por mim">
          <Checkbox
            checked={myFormsOnly}
            onChange={e => onMyFormsChange(e.target.checked)}
          >
            {t('Home.TabTitle.AdhocMyActivities')}
          </Checkbox>
        </Tooltip>
        <Tooltip title="Mostra apenas os formulários que estão abertos e prontos para serem preenchidos.">
          <Checkbox
            checked={adhocOnly}
            onChange={e => onAdhocChange(e.target.checked)}
          >
            Formulários disponíveis para responder
          </Checkbox>
        </Tooltip>
        <Tooltip title="Exibir apenas formulários marcados como favoritos">
          <Checkbox
            checked={showFavoritesOnly}
            onChange={e => onFavoritesChange(e.target.checked)}
          >
            <StarFilled style={{ color: "#facc15" }} /> {t('Home.Schedule.Favorites')}
          </Checkbox>
        </Tooltip>
      </Col>
    </Row>
  </>
));

// Pure FormCard, only re-renders on prop change
const FormCard = React.memo(({ form, onView, navigate, t }) => (
  <Card style={cardStyle} bodyStyle={bodyCardStyle}>
    <Space direction="vertical" size={6} style={{ width: "100%" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Space wrap>
          {form.bookmark && <StarFilled style={{ color: "#facc15" }} />}
          {form.isWorkflowTrigger && (
            <Tooltip title="Inicia fluxo de trabalho gerando tarefas.">
              <div
                style={{
                  backgroundColor: "#34d399",
                  color: "#fff",
                  fontWeight: 500,
                  borderRadius: 8,
                  padding: "2px 8px",
                  fontSize: 12,
                  display: "inline-block",
                  marginRight: 8,
                }}
              >
                Workflow
              </div>
            </Tooltip>
          )}
          <Text strong style={{ fontSize: 14 }}>
            {form.title}
          </Text>
        </Space>
        <Space wrap>
          <Button
            icon={<EyeOutlined />}
            type="text"
            size="small"
            onClick={() => onView(form)}
            style={{ padding: "0 8px", fontSize: 12, color: "#555" }}
          >
            {t('OpDetails')}
          </Button>
          {form.adhoc && (
            <Button
              icon={<EditOutlined />}
              type="text"
              size="small"
              onClick={() => navigate(`/formbuilder/apply/${form.id}`)}
              style={{ padding: "0 8px", fontSize: 12, color: "#555" }}
            >
              {t('OpAnswerForm')}
            </Button>
          )}
        </Space>
      </div>
      <Text style={{ fontSize: 12, color: "#555" }}>{form.name}</Text>
      {form.description && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {form.description}
        </Text>
      )}
      <Space size={4}>
        <Avatar size="small" icon={<UserOutlined />} />
        <div style={{ marginLeft: 4 }}>
          <Text style={{ fontSize: 12, color: "#888" }}>
            {t('CreatedBy')} {form.insertUserName || "Sistema"}
          </Text>
          <br />
          <Text style={{ fontSize: 12, color: "#888" }}>
            {t('Version')} {form.version}
          </Text>
        </div>
      </Space>
    </Space>
  </Card>
));

export default function FormBoard({ onSelectForm }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [selectedForm, setSelectedForm] = useState(null);
  const [forms, setForms] = useState([]);
  const [locationId, setLocationId] = useState(null);
  const [topicId, setTopicId] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [adhocOnly, setAdhocOnly] = useState(false);
  const [myFormsOnly, setMyFormsOnly] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [topicOpen, setTopicOpen] = useState(false);

  const cache = useRef(
    new CellMeasurerCache({ fixedWidth: true, defaultHeight: 140 })
  );
  const listRef = useRef(null);

  const fetchForms = useCallback(async () => {
    setIsLoading(true);
    try {
      const request = {
        locationId: locationId ? [locationId] : [],
        topicId,
        searchText,
      };
      const { data } = await Api.post("DashboardCustomFlowy/getForms", request);
      const newForms = data.response || [];
      setForms(newForms);
      cache.current.clearAll();
      listRef.current?.scrollToRow(0);
    } catch (error) {
      console.error("Erro ao buscar formulários:", error);
    } finally {
      setIsLoading(false);
    }
  }, [locationId, topicId, searchText]);

  useEffect(() => {
    const handler = setTimeout(fetchForms, 500);
    return () => clearTimeout(handler);
  }, [fetchForms]);

  useEffect(() => {
    onSelectForm(!!selectedForm);
  }, [selectedForm, onSelectForm]);

  const filteredForms = useMemo(
    () =>
      forms
        .filter(f => (showFavoritesOnly ? f.bookmark : true))
        .filter(f => (myFormsOnly ? f.insertUser == user?.id : true))
        .filter(f => (adhocOnly ? f.adhoc : true)),
    [forms, showFavoritesOnly, adhocOnly, myFormsOnly]
  );

  useEffect(() => {
    // limpa todas as medições antigas
    cache.current.clearAll();

    if (listRef.current) {
      // recomputa as alturas das linhas
      listRef.current.recomputeRowHeights();
      // força o List a redesenhar
      listRef.current.forceUpdateGrid();
      // opcional: volta a rolagem pro topo
      listRef.current.scrollToRow(0);
    }
  }, [showFavoritesOnly, adhocOnly, myFormsOnly, filteredForms.length]);

  const rowRenderer = useCallback(
    ({ index, key, parent, style }) => {
      const form = filteredForms[index];
      return (
        <CellMeasurer
          key={key}
          cache={cache.current}
          parent={parent}
          columnIndex={0}
          rowIndex={index}
        >
          {({ registerChild }) => (
            <div ref={registerChild} style={{ ...style, paddingBottom: 8 }}>
              <FormCard
                form={form}
                onView={setSelectedForm}
                navigate={navigate}
                t={t}
              />
            </div>
          )}
        </CellMeasurer>
      );
    },
    [filteredForms, navigate]
  );

  if (selectedForm) {
    return (
      <FormDetails
        formId={selectedForm}
        onClose={() => setSelectedForm(null)}
      />
    );
  }

  return (
    <>
      <Title level={3} style={{ marginBottom: 4 }}>
        {t('Schedule.Modal.Form')}
      </Title>
      <Text type="secondary">Use os filtros abaixo para refinar a lista de formulários.</Text>

      <FilterBar
        searchText={searchText}
        onSearchTextChange={setSearchText}
        topicId={topicId}
        onTopicChange={setTopicId}
        topicOpen={topicOpen}
        onTopicOpenChange={setTopicOpen}
        locationId={locationId}
        onLocationChange={setLocationId}
        adhocOnly={adhocOnly}
        onAdhocChange={setAdhocOnly}
        showFavoritesOnly={showFavoritesOnly}
        onFavoritesChange={setShowFavoritesOnly}
        myFormsOnly={myFormsOnly}
        onMyFormsChange={setMyFormsOnly}
        t={t}
      />

      <div style={{ marginTop: 12, height: 370 }}>
        {isLoading ? (
          <Col span={24}>
            <Loading top={0} />
          </Col>
        ) : filteredForms.length === 0 ? (
          <Text type="secondary">Nenhum formulário encontrado.</Text>
        ) : (
          <AutoSizer>
            {({ height, width }) => (
              <List
                ref={listRef}
                width={width}
                height={height}
                deferredMeasurementCache={cache.current}
                rowHeight={cache.current.rowHeight}
                rowCount={filteredForms.length}
                overscanRowCount={5}
                rowRenderer={rowRenderer}
              />
            )}
          </AutoSizer>
        )}
      </div>
    </>
  );
}
